import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Form, Modal, Table, Badge, InputGroup } from 'react-bootstrap';
import { FaTag, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getAllDiscountsApi, createDiscountApi, updateDiscountApi, deleteDiscountApi } from '../menu/service/api_service';

export default function DiscountMaster() {
  const token = localStorage.getItem('token');
  
  const [discounts, setDiscounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: 'amount', value: '', is_active: true });

  const fetchDiscounts = useCallback(async () => {
    try {
      const res = await getAllDiscountsApi(token);
      setDiscounts(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch discounts", "error");
    }
  }, [token]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const handleShowModal = (discount = null) => {
    if (discount) {
      setEditingId(discount.id);
      setFormData({ name: discount.name, type: discount.type, value: discount.value, is_active: discount.is_active === 1 || discount.is_active === true });
    } else {
      setEditingId(null);
      setFormData({ name: '', type: 'amount', value: '', is_active: true });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.value === '') {
      return Swal.fire("Warning", "Please fill all required fields", "warning");
    }

    try {
      if (editingId) {
        await updateDiscountApi(token, editingId, formData);
        Swal.fire("Success", "Discount updated successfully", "success");
      } else {
        await createDiscountApi(token, formData);
        Swal.fire("Success", "Discount created successfully", "success");
      }
      handleCloseModal();
      fetchDiscounts();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirmResult = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirmResult.isConfirmed) {
      try {
        await deleteDiscountApi(token, id);
        Swal.fire("Deleted!", "Discount has been deleted.", "success");
        fetchDiscounts();
      } catch (err) {
        console.error(err);
        Swal.fire("Error", err.response?.data?.message || "Failed to delete discount", "error");
      }
    }
  };

  return (
    <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100%" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: "#1e3c72" }}><FaTag className="me-2" /> Discounts Master</h2>
          <p className="text-muted mb-0 mt-1">Manage discount types and values</p>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={() => handleShowModal()}>
            <FaPlus className="me-2" /> Add Discount
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-0 bg-white" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-0">
          <Table hover responsive className="align-middle mb-0">
            <thead style={{ backgroundColor: "#f1f4f8" }}>
              <tr>
                <th className="py-3 px-4 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>ชื่อส่วนลด (Name)</th>
                <th className="py-3 px-4 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>รูปแบบ (Type)</th>
                <th className="py-3 px-4 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>มูลค่าลด (Value)</th>
                <th className="py-3 px-4 text-secondary font-weight-bold text-center" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>สถานะ (Status)</th>
                <th className="py-3 px-4 text-secondary font-weight-bold text-end" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>จัดการ (Actions)</th>
              </tr>
            </thead>
            <tbody>
              {discounts.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    <FaTag size={48} className="mb-3 opacity-25" />
                    <h5>No discounts found</h5>
                  </td>
                </tr>
              ) : (
                discounts.map(disc => (
                  <tr key={disc.id}>
                    <td className="py-3 px-4">
                      <div className="fw-bold text-dark">{disc.name}</div>
                    </td>
                    <td className="py-3 px-4">
                      {disc.type === 'amount' ? (
                        <span className="badge bg-primary">THB (บาท)</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Percentage (%)</span>
                      )}
                    </td>
                    <td className="py-3 px-4 fw-bold">
                      {disc.type === 'amount' ? `฿${Number(disc.value).toFixed(2)}` : `${Number(disc.value)}%`}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {(disc.is_active === 1 || disc.is_active === true) ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-end">
                      <Button variant="light" size="sm" className="me-2" onClick={() => handleShowModal(disc)}>
                        <FaEdit className="text-primary" />
                      </Button>
                      <Button variant="light" size="sm" onClick={() => handleDelete(disc.id)}>
                        <FaTrash className="text-danger" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered backdrop="static">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            {editingId ? 'แก้ไขส่วนลด (Edit Discount)' : 'เพิ่มส่วนลด (Add Discount)'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small">ชื่อส่วนลด (Discount Name) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="เช่น AIS Serenade, ส่วนลดพนักงาน"
                required
                className="py-2"
              />
              {editingId && (
                <Form.Text className="text-warning small">
                  * หากส่วนลดนี้เคยถูกใช้งานไปแล้ว จะไม่สามารถเปลี่ยนชื่อได้
                </Form.Text>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small">รูปแบบส่วนลด (Type) <span className="text-danger">*</span></Form.Label>
              <div className="d-flex gap-3">
                <Form.Check 
                  type="radio" 
                  id="type-amount" 
                  name="type" 
                  value="amount" 
                  label="ลดเป็นบาท (THB)" 
                  checked={formData.type === 'amount'}
                  onChange={handleChange}
                />
                <Form.Check 
                  type="radio" 
                  id="type-percentage" 
                  name="type" 
                  value="percentage" 
                  label="ลดเป็นเปอร์เซ็นต์ (%)" 
                  checked={formData.type === 'percentage'}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small">มูลค่าลดเริ่มต้น (Default Value) <span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-light">
                  {formData.type === 'amount' ? '฿' : '%'}
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name="value"
                  value={formData.value}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  className="py-2"
                />
              </InputGroup>
              <Form.Text className="text-muted small">
                * ค่านี้จะเป็นค่าเริ่มต้นในหน้า POS แต่พนักงานสามารถแก้ไขหน้างานได้
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="is-active-switch"
                name="is_active"
                label={<span className="fw-bold">เปิดใช้งาน (Active)</span>}
                checked={formData.is_active}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={handleCloseModal} className="px-4">
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" className="px-4 fw-bold shadow-sm">
              บันทึกข้อมูล
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
